// =============================================================================
// components/home/Sparkline.tsx — tiny reusable SVG sparkline.
//
// PREVIEW-ONLY today: the Signals row feeds it hardcoded demo shapes (clearly
// labeled as preview data in SignalsRow) until the v2 signals engine ships real
// series. Draws a smooth-ish polyline + a soft gradient area fill underneath,
// sized to whatever width/height the card gives it. Pure react-native-svg —
// no chart lib, no layout side effects.
// =============================================================================
import React from 'react';
import Svg, { Path, Defs, LinearGradient, Stop } from 'react-native-svg';

export interface SparklineProps {
  /** Series values (any scale — normalized internally). Needs >= 2 points. */
  data: number[];
  width?: number;
  height?: number;
  /** Line color (defaults to CRYB purple). */
  color?: string;
  strokeWidth?: number;
  /** Draw the soft gradient area under the line (default true). */
  fill?: boolean;
}

/** Build the SVG path for the normalized series. */
function linePath(data: number[], w: number, h: number, pad: number): string {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1; // flat series → centered line
  const stepX = (w - pad * 2) / (data.length - 1);
  return data
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + (1 - (v - min) / span) * (h - pad * 2);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

export default function Sparkline({
  data,
  width = 96,
  height = 36,
  color = '#6C5CE7',
  strokeWidth = 2,
  fill = true,
}: SparklineProps) {
  if (!Array.isArray(data) || data.length < 2) return null;
  const pad = strokeWidth + 1;
  const d = linePath(data, width, height, pad);
  // Close the area path down to the baseline for the gradient fill.
  const area = `${d} L${(width - pad).toFixed(2)},${height} L${pad},${height} Z`;
  // Unique-enough gradient id so multiple sparklines don't collide.
  const gid = `spark-${color.replace(/[^a-zA-Z0-9]/g, '')}-${data.length}`;

  return (
    <Svg width={width} height={height} pointerEvents="none">
      <Defs>
        <LinearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <Stop offset="0" stopColor={color} stopOpacity={0.28} />
          <Stop offset="1" stopColor={color} stopOpacity={0.02} />
        </LinearGradient>
      </Defs>
      {fill && <Path d={area} fill={`url(#${gid})`} />}
      <Path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}
